import styled from '@emotion/styled';
import {
  Box,
  ClickAwayListener,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Popper,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import InfoIcon from '@material-ui/icons/Info';
import { DateTime } from 'luxon';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useLocalStorage } from 'react-use';
import HttpClient from '../../../../adapter/HttpClient';
import AnnouncementWebApi, { Announcement } from '../../../../service/persistent/AnnouncementWebApi';
import { COLOR_MENU_BACKGROUND, COLOR_MENU_HEADER, COLOR_TEXT_MAIN } from '../../../../theme/colors';
import { context as ConfigContext } from '../../../context/config';

const ANNOUNCEMENT_SITE_URL = 'https://support.kaizenplatform.net/hc/ja';

type Props = {
  className?: string;
};

const Announcement: FC<Props> = ({ className }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [announcements, setAnnouncements] = useState<Array<Announcement>>([]);
  const config = useContext(ConfigContext);

  const [readAnnouncements, setReadAnnouncements] = useLocalStorage<Array<Announcement>>('read_announcements', []);
  const [oldReadAnnouncements, setOldReadAnnouncements] = useState<Array<Announcement>>([]);

  const onClickMenu = () => {
    setOpen(true);
  };

  const onRequestClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    const fetch = async () => {
      const httpClient = new HttpClient({
        baseUrl: config.LIFT_REPORT_API_BASE,
      });

      const client = new AnnouncementWebApi({ httpClient });
      const announcements = await client.getAnnouncements();
      setOldReadAnnouncements(readAnnouncements!);
      setAnnouncements(announcements);

      if (readAnnouncements != null && readAnnouncements[0]?.id !== announcements[0]?.id) {
        setOpen(true);
        setReadAnnouncements(announcements);
      }
    };

    fetch();
  }, []);

  const viewAnnouncements = useMemo(() => {
    return announcements.slice(0, 5).map((announcement) => ({
      ...announcement,
      startAt: DateTime.fromISO(announcement.start_at).toFormat('yyyy/MM/dd HH:mm'),
      new: !oldReadAnnouncements.some((v) => v.id === announcement.id),
    }));
  }, [announcements, oldReadAnnouncements]);

  return (
    <div className={className}>
      <IconButton size="small" onClick={onClickMenu} ref={setAnchorEl}>
        <InfoIcon style={{ color: 'white' }} />
      </IconButton>

      <Popper open={open} anchorEl={anchorEl} placement="bottom-end" transition disablePortal>
        <ClickAwayListener onClickAway={onRequestClose}>
          <Root>
            <Body button>
              <BodyText variant="body1">お知らせ</BodyText>
              <BodyIcon onClick={onRequestClose}>
                <BodyCloseIcon />
              </BodyIcon>
            </Body>
            <List>
              {viewAnnouncements.map((announcement) => (
                <ListItem key={announcement.id} divider>
                  <ListItemText>
                    <Link href={announcement.url} target="_blank">
                      <Box pb={1}>
                        <Typography variant="body1">{announcement.title}</Typography>
                      </Box>
                    </Link>
                    <Typography variant="body2">
                      {announcement.new && 'NEW '}
                      {announcement.startAt}
                    </Typography>
                  </ListItemText>
                </ListItem>
              ))}
            </List>
            <AnnouncementItem button>
              <Link href={ANNOUNCEMENT_SITE_URL} target="_blank">
                <AnnouncementText variant="body1">すべて見る</AnnouncementText>
              </Link>
            </AnnouncementItem>
          </Root>
        </ClickAwayListener>
      </Popper>
    </div>
  );
};

const Root = styled(Paper)`
  border-radius: 0;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.16);
  width: 320px;
  background-color: ${COLOR_MENU_BACKGROUND};
`;

const Body = styled(ListItem)`
  height: 48;
  display: flex;
  justify-content: space-between;
  background-color: ${COLOR_MENU_HEADER};
`;

const BodyText = styled(Typography)`
  position: static;
  font-size: 16px;
  font-weight: 800;
  color: ${COLOR_TEXT_MAIN};
`;

const BodyIcon = styled(IconButton)`
  width: 28px;
  height: 28px;
`;

const BodyCloseIcon = styled(CloseIcon)`
  color: ${COLOR_TEXT_MAIN};
  width: 24px;
  height: 24px;
`;

const AnnouncementItem = styled(ListItem)`
  height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${COLOR_MENU_HEADER};
`;

const AnnouncementText = styled(Typography)`
  font-size: 16px;
  font-weight: 800;
  color: ${COLOR_TEXT_MAIN};
`;

export default Announcement;
